/**
 * Colour maths for the calendar chip palette. See docs/calendar-activity-colors.md.
 *
 * OKLCH rather than HSL, whose lightness is not perceptually uniform. TypeScript rather
 * than CSS `color-mix()`, which silently yields nothing on older WKWebView.
 */

export interface Rgb { r: number; g: number; b: number; }
export interface Oklch { l: number; c: number; h: number; }

const HEX_RE = /^#([0-9a-f]{6})$/i;

/** Strict `#RRGGBB` check. Deliberately narrow: config values come from an admin
 *  free-text field and must never reach a style binding unvalidated. */
export function isValidHex(value: unknown): value is string {
  return typeof value === 'string' && HEX_RE.test(value.trim());
}

export function hexToRgb(hex: string): Rgb | null {
  const match = HEX_RE.exec(hex.trim());
  if (!match) return null;
  const int = parseInt(match[1], 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

export function rgbToHex({ r, g, b }: Rgb): string {
  const to2 = (v: number) => clamp(Math.round(v), 0, 255).toString(16).padStart(2, '0');
  return `#${to2(r)}${to2(g)}${to2(b)}`;
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

// sRGB <-> linear

function srgbToLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function linearToSrgb(channel: number): number {
  const c = channel <= 0.0031308
    ? channel * 12.92
    : 1.055 * Math.pow(channel, 1 / 2.4) - 0.055;
  return clamp(c * 255, 0, 255);
}

// OKLab / OKLCH (Bjorn Ottosson's matrices)

export function rgbToOklch(rgb: Rgb): Oklch {
  const r = srgbToLinear(rgb.r);
  const g = srgbToLinear(rgb.g);
  const b = srgbToLinear(rgb.b);

  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);

  const okL = 0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s;
  const okA = 1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s;
  const okB = 0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s;

  return {
    l: okL,
    c: Math.sqrt(okA * okA + okB * okB),
    h: Math.atan2(okB, okA),
  };
}

export function oklchToRgb({ l, c, h }: Oklch): Rgb {
  const okA = c * Math.cos(h);
  const okB = c * Math.sin(h);

  const l_ = l + 0.3963377774 * okA + 0.2158037573 * okB;
  const m_ = l - 0.1055613458 * okA - 0.0638541728 * okB;
  const s_ = l - 0.0894841775 * okA - 1.2914855480 * okB;

  const lc = l_ * l_ * l_;
  const mc = m_ * m_ * m_;
  const sc = s_ * s_ * s_;

  return {
    r: linearToSrgb(4.0767416621 * lc - 3.3077115913 * mc + 0.2309699292 * sc),
    g: linearToSrgb(-1.2684380046 * lc + 2.6097574011 * mc - 0.3413193965 * sc),
    b: linearToSrgb(-0.0041960863 * lc - 0.7034186147 * mc + 1.7076147010 * sc),
  };
}

// Contrast (WCAG 2.x relative luminance)

export function relativeLuminance(rgb: Rgb): number {
  return 0.2126 * srgbToLinear(rgb.r)
    + 0.7152 * srgbToLinear(rgb.g)
    + 0.0722 * srgbToLinear(rgb.b);
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

// Ink derivation - the only computed colour in the feature

/** Tuning for {@link deriveInk}. Chroma is capped, not preserved: at the extremes a
 *  full-chroma hue clips in sRGB or stops reading as its own fill. */
export const INK_DERIVATION = {
  /** WCAG AA for normal text. The chip title is 13px/500 - normal, not large. */
  targetRatio: 4.5,
  /** First step away from the fill, so the ink is never almost-the-fill. */
  initialOffset: 0.02,
  step: 0.01,
  darkFloor: 0.10,
  lightCeiling: 0.97,
  darkChromaCap: 0.16,
  lightChromaCap: 0.10,
  /** For a fill that cannot reach `targetRatio` either way - a mid-tone near luminance
   *  0.15, where only pure white clears 4.5:1. Keeps the hue instead of going white. */
  relaxedRatio: 4.0,
} as const;

const BLACK: Rgb = { r: 0, g: 0, b: 0 };
const WHITE: Rgb = { r: 255, g: 255, b: 255 };

/** Round to whole channels. What renders is an 8-bit hex, so contrast has to be measured
 *  on the rounded value, not on the float the conversion produced. */
function quantise({ r, g, b }: Rgb): Rgb {
  return { r: Math.round(r), g: Math.round(g), b: Math.round(b) };
}

/**
 * A readable variant of `source`, kept in its own hue. Walks lightness DOWN until it
 * clears `targetRatio` against `against`; out of room - a dark fill - it reverses and
 * goes UP, giving a lighter tint rather than flat white.
 *
 * `against` is the surface the colour is READ ON, defaulting to the source itself.
 */
export function deriveInk(
  source: Rgb,
  against: Rgb = source,
  targetRatio: number = INK_DERIVATION.targetRatio,
): Rgb {
  // Already readable as-is. Unreachable when against === source, since a colour has
  // contrast 1:1 with itself.
  if (contrastRatio(source, against) >= targetRatio) return quantise(source);

  const { l, c, h } = rgbToOklch(source);
  const { initialOffset, step, darkFloor, lightCeiling, darkChromaCap, lightChromaCap, relaxedRatio } = INK_DERIVATION;

  const darker = (li: number) => quantise(oklchToRgb({ l: li, c: Math.min(c, darkChromaCap), h }));
  const lighter = (li: number) => quantise(oklchToRgb({ l: li, c: Math.min(c, lightChromaCap), h }));

  // Pass 1, the real target. Darker first: every shipped default clears it this way.
  for (let li = l - initialOffset; li >= darkFloor; li -= step) {
    const candidate = darker(li);
    if (contrastRatio(candidate, against) >= targetRatio) return candidate;
  }
  for (let li = l + initialOffset; li <= lightCeiling; li += step) {
    const candidate = lighter(li);
    if (contrastRatio(candidate, against) >= targetRatio) return candidate;
  }

  // Pass 2, the relaxed target, walking toward whichever end has headroom - otherwise a
  // mid-dark fill returns a near-black ink that passes and reads terribly.
  const preferLighter = contrastRatio(WHITE, against) >= contrastRatio(BLACK, against);
  if (preferLighter) {
    for (let li = l + initialOffset; li <= lightCeiling; li += step) {
      const candidate = lighter(li);
      if (contrastRatio(candidate, against) >= relaxedRatio) return candidate;
    }
  } else {
    for (let li = l - initialOffset; li >= darkFloor; li -= step) {
      const candidate = darker(li);
      if (contrastRatio(candidate, against) >= relaxedRatio) return candidate;
    }
  }

  // Not reachable for any sRGB pair - one extreme always beats 4.0:1 against a mid-tone.
  // Kept so the function is total.
  return preferLighter ? WHITE : BLACK;
}

/** Hex-in, hex-out convenience for callers that never touch {@link Rgb}. */
export function deriveInkHex(fillHex: string, targetRatio?: number): string | null {
  const fill = hexToRgb(fillHex);
  if (!fill) return null;
  return rgbToHex(deriveInk(fill, fill, targetRatio));
}
